'use client';

import React, {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import logo from '@/assets/images/logo.png';
import Image from 'next/image';
import Button from '@/components/atoms/button';
import Icon from '@/components/atoms/icon';
import Drawer from '@/components/molecules/drawer';
import { useData } from '@/stores/context';
import Skeleton from '@/components/atoms/skeletons';
import Link from 'next/link';
import { mapModifiers } from '@/utils/funtion';
import { FiChevronDown } from 'react-icons/fi';
import UserDropdown from '@/components/atoms/userDropdown';
import { useSelector } from 'react-redux';
import { FaCartShopping } from 'react-icons/fa6';
import { signOut } from '@/services/apis/modules/auth';
import { IoMailSharp } from 'react-icons/io5';
import { FaPhoneAlt } from 'react-icons/fa';
import { FaBars } from 'react-icons/fa';
import { signOut as signOutNextAuth } from 'next-auth/react';

interface MenuItemProps {
  item: any;
  isActive: boolean;
  onHover: () => void;
  onLeave: () => void;
}

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  isActive,
  onHover,
  onLeave,
}) => {
  const handleClick = (e: React.MouseEvent, slug: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.href = slug;
  };

  return (
    <li
      className={mapModifiers(
        'app-header_menu_item',
        isActive && item.hasChild && 'hover',
      )}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={(e) => handleClick(e, item?.slug)}
      style={{ cursor: 'pointer' }}
    >
      <Link href={item?.slug} style={{ display: 'flex', width: '100%' }}>
        <p>{item?.title}</p>
        {item?.hasChild && <Icon component={<FiChevronDown size={26} />} />}
      </Link>
      {item?.hasChild && (
        <ul onClick={(e) => e.stopPropagation()}>
          {item?.child?.map((child: any, index: number) => (
            <li
              key={index}
              className={mapModifiers(
                child?.hasChild && 'hover' && 'has-inner-child',
              )}
              onClick={(e) => handleClick(e, child.slug)}
              style={{ cursor: 'pointer' }}
            >
              <Link href={child.slug} style={{ width: '100%' }}>
                <p>{child.title}</p>
                {child.hasChild && (
                  <Icon component={<FiChevronDown size={16} />} />
                )}
              </Link>
              {child.hasChild && (
                <ul
                  className="inner-child-menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {child.child.map((subChild: any, i: number) => (
                    <li
                      key={i}
                      onClick={(e) => handleClick(e, subChild.slug)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Link
                        href={subChild.slug}
                        style={{ display: 'block', width: '100%' }}
                      >
                        <p>{subChild.title}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

interface HeaderClientProps {
  initialData: {
    menuConfig: any;
    contact: {
      PHONE: string;
      EMAIL: string;
    };
  };
}

const HeaderClient: React.FC<HeaderClientProps> = ({ initialData }) => {
  const { menuConfig, contact } = initialData;
  const [menu, setMenu] = useState({
    idActive: 0,
    idHover: undefined as number | undefined,
    idExpand: undefined as number | undefined,
    openMenuMobile: false,
  });
  const refMenuMobile = useRef<HTMLUListElement | null>(null);
  const { innerWidth } = useData();

  const userData = useSelector((state: any) => state.userData);
  const [isLoading, setIsLoading] = useState(true);
  const [isScroll, setIsScroll] = useState(false);

  useLayoutEffect(() => {
    setIsLoading(false);
  }, []);

  const handleLogOut = async () => {
    await signOut().then(async () => {
      await signOutNextAuth();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/xac-thuc-nguoi-dung';
    });
  };

  useEffect(() => {
    const body: HTMLElement = document.querySelector(
      '.app-home',
    ) as HTMLElement;
    if (!body) return;
    function handleListenEventScroll(event: Event) {
      const scrollTop = (event.currentTarget as any).scrollTop;
      setIsScroll(scrollTop > Number(innerWidth < 600 ? 50 : 250));
    }
    body.addEventListener('scroll', handleListenEventScroll);
    return () => body.removeEventListener('scroll', handleListenEventScroll);
  }, []);

  const handleChooseOption = async (type: string) => {
    switch (type) {
      case 'info':
        window.location.href = `/trang-khach-hang`;
        break;
      case 'logout':
        await handleLogOut();
        break;
      default:
        break;
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    window.location.href = url;
  };

  return (
    <React.Fragment>
      {isScroll && innerWidth > 960 && (
        <div style={{ height: 185, width: '100%' }}></div>
      )}
      <header className={mapModifiers('app-header', isScroll && 'scroll')}>
        <div className="app-header_wrapper">
          <div className="app-header_bottom">
            <div className="app-header_bottom_contact">
              <Link href={`tel:${contact.PHONE}`}>
                <FaPhoneAlt />
                <p>{contact.PHONE}</p>
              </Link>
              <Link href={`mailto:${contact.EMAIL}`}>
                <IoMailSharp />
                <p>{contact.EMAIL}</p>
              </Link>
            </div>

            <div
              className={mapModifiers(
                'app-header_bottom_action',
                !userData?.user ? '' : 'signed',
                innerWidth > 680 ? 'tablet' : 'mobile',
              )}
            >
              {!userData?.user ? (
                innerWidth > 1360 && (
                  <Link href="/xac-thuc-nguoi-dung" aria-label="Đăng ký">
                    {innerWidth > 680 ? (
                      'Đăng ký/ Đăng nhập'
                    ) : (
                      <Icon iconName="fingerprint" />
                    )}
                  </Link>
                )
              ) : (
                <React.Fragment>
                  {innerWidth > 680 && (
                    <UserDropdown
                      name={userData?.user?.full_name}
                      handleClickOption={handleChooseOption}
                    />
                  )}
                </React.Fragment>
              )}
            </div>
          </div>
          <div className="app-header_top">
            <div className="app-header_logo">
              <Suspense
                fallback={
                  <Skeleton
                    active
                    loading
                    type="avatar"
                    round
                    shape="circle"
                    sizeAvatar={60}
                    fullSize
                  />
                }
              >
                <Link href="/" onClick={(e) => handleClick(e, '/')}>
                  <Image src={logo} alt="Logo công ty" />
                  <div className="app-header_logo_slogan">
                    Let Data Lead The Policy
                  </div>
                </Link>
              </Suspense>
            </div>
            <ul
              className={mapModifiers(
                'app-header_menu',
                innerWidth < 1360 ? 'disable' : 'active',
              )}
            >
              {menuConfig.map((item: any) => (
                <React.Fragment key={item.id}>
                  <MenuItem
                    item={item}
                    isActive={item.id === menu.idHover}
                    onHover={() =>
                      setMenu((prev) => ({ ...prev, idHover: item.id }))
                    }
                    onLeave={() =>
                      setMenu((prev) => ({ ...prev, idHover: undefined }))
                    }
                  />
                </React.Fragment>
              ))}
            </ul>
            <div
              className={mapModifiers(
                'app-header_action',
                !userData?.user ? '' : 'signed',
                innerWidth > 680 ? 'tablet' : 'mobile',
              )}
            >
              {isLoading ? (
                <div
                  className="app-header_action_loading"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                  }}
                >
                  <Skeleton active loading type="input" width={100} />
                  <Skeleton active loading type="input" width={100} />
                </div>
              ) : (
                <>
                  {innerWidth > 680 && (
                    <Link
                      href="/cac-goi-du-lieu"
                      aria-label="Đăng ký"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        textDecoration: 'none',
                      }}
                      onClick={(e) => handleClick(e, '/cac-goi-du-lieu')}
                    >
                      <Button
                        shape="fill"
                        variant="secondary"
                        ariaLabel="Gói dữ liệu"
                        className="plans-button"
                        style={{ padding: '6px 4px', width: 'fit-content' }}
                      >
                        <FaCartShopping
                          style={{ fontSize: 18, marginLeft: 4 }}
                        />{' '}
                        Gói dữ liệu
                      </Button>
                    </Link>
                  )}
                  {innerWidth <= 1360 && (
                    <Icon
                      className="app-header_action_icon"
                      component={<FaBars />}
                      onClick={() =>
                        setMenu((prev) => ({ ...prev, openMenuMobile: true }))
                      }
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <Drawer
          open={menu.openMenuMobile}
          onClose={() =>
            setMenu((prev) => ({ ...prev, openMenuMobile: false }))
          }
          footer={false}
          width={innerWidth < 420 ? innerWidth : 420}
          title={
            <Link href="/trang-khach-hang" className="app-header_profile-menu">
              <Icon iconName={'menu_user'} size={'17x17'} />
              {userData?.user?.full_name
                ? userData?.user?.full_name
                : 'Vietstats'}
            </Link>
          }
        >
          <ul className="app-header_menu-mobile" ref={refMenuMobile}>
            <li className={mapModifiers('app-header_menu-mobile_item')}>
              <Link
                href="/"
                onClick={() =>
                  setMenu((prev) => ({ ...prev, openMenuMobile: false }))
                }
              >
                <p>
                  <Icon iconName={'menu_home'} size={'17x17'} />
                  Trang chủ
                </p>
              </Link>
            </li>
            {menuConfig.map((item: any) => (
              <li
                key={item.id}
                className={mapModifiers(
                  'app-header_menu-mobile_item', 'expand'
                  // item.id === menu.idExpand && 'expand',
                )}
              >
                <div
                  onClick={() =>
                    setMenu((prev) => ({
                      ...prev,
                      idExpand: prev.idExpand === item.id ? undefined : item.id,
                    }))
                  }
                >
                  <Link href={item.hasChild ? '#' : item.slug}>
                    <p>
                      <Icon iconName={item.icon as any} size={'17x17'} />
                      {item.title}
                    </p>
                    {/* {item.hasChild && <Icon component={<FiChevronDown />} />} */}
                  </Link>
                </div>
                {item.hasChild && (
                  <ul>
                    {item?.child?.map((child: any) => (
                      <li key={child.id}>
                        <Link href={child.slug}>
                          <p>{child.title}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <li className={mapModifiers('app-header_menu-mobile_item')}>
              <Link href="/plans">
                <p>
                  <Icon iconName={'menu_cart'} size={'17x17'} />
                  Các gói dữ liêu
                </p>
              </Link>
            </li>
            {!userData?.user ? (
              <li className={mapModifiers('app-header_menu-mobile_item')}>
                <Link href="/auth">
                  <p>
                    <Icon iconName={'menu_log'} size={'17x17'} />
                    Đăng nhập/ Đăng ký
                  </p>
                </Link>
              </li>
            ) : (
              <li
                className={mapModifiers('app-header_menu-mobile_item')}
                onClick={handleLogOut}
              >
                <p>
                  <Icon iconName={'menu_log'} size={'17x17'} />
                  Đăng xuất
                </p>
              </li>
            )}
          </ul>
        </Drawer>
      </header>
    </React.Fragment>
  );
};

export default HeaderClient;
